import {
  Box3,
  Camera,
  LinearFilter,
  OrthographicCamera,
  Scene,
  Sprite,
  SpriteMaterial,
  Texture,
  WebGLRenderer,
} from "three";
import { Vector3 } from "three/src/Three";
import { Quick3DPerspectiveCamera } from "../camera/quick3d-perspective-camera";
import { ITextScale } from "../config/ViewerConfig";
import { AutoAttenuableState } from "../measurement/types";
import { AutoAttenuableLine } from "./AutoAttenuableLine";
import { AutoAttenuatableSphere } from "./AutoAttenuatableSphere";

/**
 * Use it by adding under a group
 */
export class AutoAttenuatableTextSprite extends Sprite {
  public aspect: number = 1;
  private _position: Vector3;
  private _orthographicScaleConst: number;
  private _perspectiveScaleConst: number;
  private _isAdjusted: boolean;

  private _textSize: number = 3;
  /**
   * Size of the text.
   * Base value is 0.2.
   */
  public get textSize() {
    return this._textSize;
  }
  public set textSize(value: number) {
    this._textSize = value;
  }

  constructor(textTexture: Texture, textScale: ITextScale) {
    super();

    this._position = new Vector3();
    this.material = new SpriteMaterial({
      map: textTexture,
      sizeAttenuation: false,
      depthTest: false,
      transparent: true,
    });
    this.material.map.minFilter = LinearFilter;
    this.renderOrder = 2;
    this.geometry = this.geometry.clone();
    this.geometry.computeBoundingSphere();
    this._orthographicScaleConst = textScale?.orthographic;
    this._perspectiveScaleConst = textScale?.perspective;
    this._isAdjusted = false;
    this.onBeforeRender = function (
      _ifcRenderer: WebGLRenderer,
      _nonClippingScene: Scene,
      camera: Camera
    ): void {
      let scale = 1;
      const windowScale = Math.sqrt(
        (1280 * 720) / (window.screen.width * window.screen.height)
      );

      if (camera instanceof OrthographicCamera) {
        scale =
          ((camera.right - camera.left) / camera.zoom) *
          this._orthographicScaleConst *
          windowScale;
      }
      if (camera instanceof Quick3DPerspectiveCamera) {
        scale = this._perspectiveScaleConst * windowScale;
      }
      this.scale.set(scale, scale * this.aspect, scale);
      this.updateMatrixWorld();
      this.geometry.computeBoundingBox();

      const placebelowLowestPoint = (point1: Vector3, point2: Vector3) => {
        const point1Projection = point1.clone().project(camera);
        const point2Projection = point2.clone().project(camera);

        const combinedProjectedBox = new Box3().setFromPoints([
          point1Projection,
          point2Projection,
        ]);
        const combinedSize = combinedProjectedBox.getSize(new Vector3());
        if (scale * 0.05 > combinedSize.y && scale * 0.1 > combinedSize.x) {
          const lowestPoint =
            point1Projection.y < point2Projection.y
              ? point1.clone()
              : point2.clone();
          const down = camera.up.clone().negate().normalize();
          this.position.copy(lowestPoint.addScaledVector(down, scale * 0.3));
        } else {
          this.position.copy(this._position);
        }
      };

      const lines = this.parent.children.filter(
        (c) => c instanceof AutoAttenuableLine
      ) as AutoAttenuableLine[];
      if (lines.length > 0) {
        if (lines.length === 1) {
          placebelowLowestPoint(lines[0].points[0], lines[0].points[1]);
        }
      } else {
        const spheres = this.parent.children.filter(
          (c) => c instanceof AutoAttenuatableSphere
        );
        if (spheres.length === 2) {
          placebelowLowestPoint(spheres[0].position, spheres[1].position);
        }
      }
    };
  }

  public dispose(): void {
    if (this.parent) {
      this.parent.remove(this);
    }

    this.geometry?.dispose();

    if (Array.isArray(this.material)) {
      for (const m of this.material) {
        m.dispose();
      }
    } else {
      this.material?.dispose();
    }
    this.geometry = undefined;
    this.material = undefined;
  }

  setPosition(position: Vector3) {
    this.position.copy(position);
    this._position.copy(position);
  }

  adjustScaleConst(c: number) {
    if (!this._isAdjusted) {
      this._orthographicScaleConst *= c;
      this._perspectiveScaleConst *= c;
      this._isAdjusted = true;
    }
  }

  getPosition() {
    return this._position;
  }

  public changeRenderOrderByState(state: AutoAttenuableState) {
    this.renderOrder = {
      default: 2,
      hovered: 2,
      selected: 3,
    }[state];
  }
}
